import debounce from 'debounce';

const pluginName = 'startGroupsPlugin';
const defaults = {
  eventId: null,
  registrantPersonalInformation: null,
  registrantUuid: null,
  maxRetries: 2,
  routes: {
    validation: null,
  },
  callbacks: {
    initialized: () => {},
    setup: () => {},
    getGivenName: () => '',
    getFamilyName: () => '',
    getDateOfBirth: () => '',
  },
  i18n: {},
};
const selectors = {
  selectContainer: '[data-js-start-group-select-container]',
  select: '[data-js-start-group-select]',
  selectFeedback: '[data-js-start-group-select-feedback]',
  selectFeedbackPending: '[data-js-start-group-select-feedback-pending]',
  selectFeedbackVerified: '[data-js-start-group-select-feedback-verified]',
  selectFeedbackFailed: '[data-js-start-group-select-feedback-failed]',
  selectFeedbackFailedUrl: '[data-js-start-group-select-feedback-failed-url]',
  urlVerificationContainer: '[data-js-start-group-url-verification-container]',
  urlVerification: '[data-js-start-group-url-verification]',
  retry: '[data-js-start-group-retry]',
  retryIcon: '[data-js-start-group-retry-icon]',
};
const classes = {
  hidden: 'd-none hidden',
  hasSuccess: 'has-success',
  hasError: 'has-error',
};

/**
 * Get the elements
 * @param data
 * @returns {{}}
 */
const getElements = (data) => {
  const $selectContainer = data.elements.$element.find(selectors.selectContainer);
  const $selectFeedback = $selectContainer.find(selectors.selectFeedback);
  const $urlVerificationContainer = data.elements.$element.find(selectors.urlVerificationContainer);

  return {
    $selectContainer,
    $select: $selectContainer.find(selectors.select),
    $selectFeedback,
    $selectFeedbackPending: $selectFeedback.find(selectors.selectFeedbackPending),
    $selectFeedbackVerified: $selectFeedback.find(selectors.selectFeedbackVerified),
    $selectFeedbackFailed: $selectFeedback.find(selectors.selectFeedbackFailed),
    $selectFeedbackFailedUrl: $selectFeedback.find(selectors.selectFeedbackFailedUrl),
    $urlVerificationContainer,
    $urlVerification: $urlVerificationContainer.find(selectors.urlVerification),
    $retry: data.elements.$element.find(selectors.retry),
  };
};

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

const getFields = (data) => [
  {
    field: 'given_name',
    value: data.settings.registrantPersonalInformation.person.givenName || data.settings.callbacks.getGivenName(),
  },
  {
    field: 'family_name',
    value: data.settings.registrantPersonalInformation.person.familyName || data.settings.callbacks.getFamilyName(),
  },
  {
    field: 'dob',
    value: data.settings.registrantPersonalInformation.person.dateOfBirth ?? data.settings.callbacks.getDateOfBirth(),
  },
];

/**
 * Toggle the visibility of the plugin
 * @param data
 * @param isVisible
 */
const toggleVisibility = (data, isVisible) => {
  data.elements.$element.toggleClass(classes.hidden, !isVisible);
};

/**
 * Toggle the visibility of the URL verification
 * @param data
 * @param isVisible
 */
const toggleUrlVerification = (data, isVisible) => {
  data.elements.$urlVerificationContainer.toggleClass(classes.hidden, !isVisible);
  data.elements.$urlVerification.attr('disabled', !isVisible);
};

/**
 * Reset the start group state
 * @param data
 */
const resetStartGroupState = (data) => {
  data.elements.$selectContainer
    .removeClass(classes.hasSuccess)
    .removeClass(classes.hasError);
  data.elements.$selectFeedback.addClass(classes.hidden);
  data.elements.$selectFeedbackPending.addClass(classes.hidden);
  data.elements.$selectFeedbackVerified.addClass(classes.hidden);
  data.elements.$selectFeedbackFailed.addClass(classes.hidden);
  data.elements.$selectFeedbackFailedUrl.addClass(classes.hidden);
  toggleUrlVerification(data, false);
};

/**
 * On start group select
 * @param data
 */
const validateStartGroupSelection = (data) => {
  const startGroup = data.elements.$select?.val();

  if (!startGroup) {
    return;
  }

  const startGroupOption = data.elements.$select.find(`option[value="${startGroup}"]`)[0];
  const listVerification = startGroupOption.hasAttribute('data-list-verification');
  const urlVerification = startGroupOption.hasAttribute('data-url-verification');

  resetStartGroupState(data);

  data.elements.$retry.preventDoubleSubmission('disable');

  if (listVerification) {
    data.elements.$selectFeedback.removeClass(classes.hidden);
    data.elements.$selectFeedbackPending.removeClass(classes.hidden);

    if (data.requests.validate) {
      data.requests.validate.abort();
    }

    data.requests.validate = $.ajax({
      url: data.settings.routes.validation,
      method: 'POST',
      data: {
        registrant_uuid: data.settings.registrantUuid,
        'start-groups[start_group_uuid]': startGroup,
        'start-groups[fields]': getFields(data),
      },
    })
      .done(() => {
        data.elements.$selectFeedbackVerified.removeClass(classes.hidden);
        data.elements.$selectContainer.addClass(classes.hasSuccess);
        data.elements.$retry.addClass(classes.hidden);
      })
      .fail(() => {
        if (urlVerification) {
          data.elements.$selectFeedbackFailedUrl.removeClass(classes.hidden);
          toggleUrlVerification(data, true);
        } else {
          data.elements.$selectFeedbackFailed.removeClass(classes.hidden);
        }

        data.elements.$selectContainer.addClass(classes.hasError);
      })
      .always(() => {
        data.requests.validate = null;
        data.elements.$selectFeedbackPending.addClass(classes.hidden);
        data.elements.$retry
          .preventDoubleSubmission('reEnable')
          .toggleClass(classes.hidden, data.state.retries >= data.settings.maxRetries);
      });
  } else if (urlVerification) {
    data.elements.$retry.addClass(classes.hidden);
    toggleUrlVerification(data, true);
  } else {
    data.elements.$retry.addClass(classes.hidden);
  }
};

/**
 * Update the start group selection
 * @param data
 */
const update = debounce((data) => {
  validateStartGroupSelection(data);
}, 200);

/**
 * Serialize the start group selection
 * @param data
 * @returns {string}
 */
const serialize = (data) => {
  const formData = data.elements.$element.serializeArray();

  const fields = getFields(data);

  fields.forEach((field, index) => {
    formData.push({ name: `start-groups[fields][${index}][field]`, value: field.field });
    formData.push({ name: `start-groups[fields][${index}][value]`, value: field.value });
  });

  return $.param(formData);
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = (data) => {
  if (data.requests.validate) {
    data.requests.validate.abort();
  }
};

/**
 * Clear the plugin's contents
 */
const clear = (data) => {
  cancelActiveRequests(data);
  data.elements.$element.empty();
};

/**
 * Setup the plugin
 * @param data
 * @param html
 */
const setup = (data, html) => {
  data.elements.$element.html(html);

  $.extend(data.elements, getElements(data));

  data.elements.$select
    .select2({
      width: '100%',
      templateResult: (startGroup) => {
        let template = startGroup.text;

        if (startGroup.id) {
          const urlVerification = startGroup.element.hasAttribute('data-url-verification');
          const listVerification = startGroup.element.hasAttribute('data-list-verification');
          const full = startGroup.element.hasAttribute('data-full');

          if (urlVerification || listVerification) {
            template += `<span class="badge badge-danger badge-pill ml-1">
              ${data.settings.i18n.startGroups.verificationRequired}
            </span>`;
          }

          if (full) {
            template += `<span class="badge badge-secondary badge-pill ml-1">
              ${data.settings.i18n.startGroups.full}
            </span>`;
          }
        }

        return $(`<div class="">${template}</div>`);
      },
    })
    .on('change', () => {
      validateStartGroupSelection(data);
    });

  data.elements.$retry
    .preventDoubleSubmission({
      spinnerReplacee: selectors.retryIcon,
    })
    .on('click', () => {
      data.state.retries += 1;
      validateStartGroupSelection(data);
    });

  resetStartGroupState(data);

  data.setup = true;

  triggerHook(data, 'setup');
};

const methods = {
  toggleVisibility,
  setup,
  initializeContent: setup,
  update,
  clear,
  cancelActiveRequests,
};

const getMethods = {
  serialize: (data) => {
    if (!data.setup) {
      return '';
    }

    return serialize(data);
  },
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const elements = {
    $element,
  };
  const requests = {
    validate: null,
  };
  const state = {
    retries: 0,
  };
  const data = {
    settings,
    elements,
    requests,
    state,
    setup: false,
  };

  data.elements.$element.data(pluginName, data);
  triggerHook(data, 'initialized');
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

plugin.methods = methods;
plugin.getMethods = getMethods;

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
